import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  Briefcase, Upload, FileText, Clock, User, Mail, Phone,
  Loader2, CheckCircle2, AlertTriangle, MapPin, Shield, ArrowRight
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { getPublicTestInfo, submitPublicTestApplication } from '../services/api';

const AGREEMENT_TEXT = `By proceeding with this assessment, you acknowledge and agree to the following:

1. Identity Verification: The information you provide (name, email, phone, resume) is accurate and belongs to you.

2. Recording & Monitoring: Your interview session may be recorded (video/audio) and monitored for proctoring purposes. Tab switches, copy-paste attempts, and other suspicious activities will be flagged.

3. Original Work: All answers must be your own. Use of external assistance, AI tools, or unauthorized resources during the assessment is strictly prohibited.

4. Data Usage: Your resume, responses, and performance data will be shared with the hiring team for evaluation purposes only.

5. Time Limits: Each question has a time limit. Unanswered questions will be submitted automatically when time expires.

6. Technical Requirements: You need a working webcam, microphone, and a stable internet connection. Use Google Chrome for the best experience.`;

export default function PublicTestPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [jobInfo, setJobInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('register'); // register | agreement | submitting | success

  useEffect(() => {
    fetchTestInfo();
  }, [token]);

  const fetchTestInfo = async () => {
    try {
      setLoading(true);
      const data = await getPublicTestInfo(token);
      setJobInfo(data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'This test link is invalid or has expired.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|doc|docx)$/i)) {
      toast.error('Please upload a PDF or Word document');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10MB');
      return;
    }
    setResumeFile(file);
  };

  const handleRegisterSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (!email.trim() || !email.includes('@')) { toast.error('Please enter a valid email'); return; }
    if (jobInfo?.resume_required && !resumeFile) { toast.error('Please upload your resume — it is required for this position'); return; }
    setStep('agreement');
  };

  const handleAgreementAccept = async () => {
    if (!agreed) { toast.error('Please accept the agreement to continue'); return; }
    setStep('submitting');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('email', email.trim());
      formData.append('phone', phone.trim());
      if (resumeFile) {
        formData.append('resume', resumeFile);
      }
      const result = await submitPublicTestApplication(token, formData);
      if (result.status === 'existing') {
        toast.info(result.message);
        setTimeout(() => navigate(result.interview_url), 1500);
        return;
      }
      setStep('success');
      toast.success('Registration complete!');
      setTimeout(() => navigate(result.interview_url), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to submit application';
      toast.error(detail);
      setStep('agreement');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
        <Toaster richColors position="top-center" />
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center px-4" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
        <Toaster richColors position="top-center" />
        <Card className="max-w-md w-full bg-[#1e293b] border-red-500/20">
          <CardContent className="py-12 text-center">
            <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-7 w-7 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-slate-100 mb-2">Link Unavailable</h2>
            <p className="text-slate-400 text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f172a]" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
      <Toaster richColors position="top-center" />

      {/* Header Bar */}
      <div className="border-b border-slate-800 bg-[#0f172a]/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
            <Briefcase className="h-4.5 w-4.5 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-100">HireFast AI</p>
            <p className="text-[11px] text-slate-500">Candidate Assessment Portal</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Job Info Card */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-3">{jobInfo.job_title}</h1>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Badge className="bg-indigo-500/10 text-indigo-300 border-indigo-500/20 text-xs px-3 py-1">{jobInfo.job_type}</Badge>
            {jobInfo.city && (
              <span className="text-sm text-slate-400 flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" /> {jobInfo.city}{jobInfo.country ? `, ${jobInfo.country}` : ''}
              </span>
            )}
            {jobInfo.client_name && (
              <span className="text-sm text-slate-400">{jobInfo.client_name}</span>
            )}
          </div>
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <FileText className="h-3.5 w-3.5" /> {jobInfo.question_count} questions
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> ~{jobInfo.total_time_minutes} min
            </span>
          </div>
        </div>

        {/* Step: Registration Form */}
        {step === 'register' && (
          <Card className="bg-[#1e293b] border-slate-700/50" data-testid="public-test-register-card">
            <CardContent className="p-6 sm:p-8">
              <div className="mb-6">
                <h2 className="text-lg font-bold text-slate-100 mb-1">Start Your Assessment</h2>
                <p className="text-sm text-slate-400">Fill in your details to begin{jobInfo?.resume_required ? '' : '. Resume upload is optional'}.</p>
              </div>
              <form onSubmit={handleRegisterSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 text-slate-500" /> Full Name
                  </Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    className="bg-[#0f172a] border-slate-600 text-slate-100 placeholder:text-slate-600 h-11"
                    data-testid="public-test-name-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 text-slate-500" /> Email Address
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@example.com"
                    className="bg-[#0f172a] border-slate-600 text-slate-100 placeholder:text-slate-600 h-11"
                    data-testid="public-test-email-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 text-slate-500" /> Phone Number <span className="text-slate-600">(optional)</span>
                  </Label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 234 567 8900"
                    className="bg-[#0f172a] border-slate-600 text-slate-100 placeholder:text-slate-600 h-11"
                    data-testid="public-test-phone-input"
                  />
                </div>

                {/* Resume Upload */}
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5 text-slate-500" /> Resume {jobInfo?.resume_required
                      ? <span className="text-red-400 text-xs">*</span>
                      : <span className="text-slate-600">(optional)</span>
                    }
                  </Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="public-test-resume-file-input"
                  />
                  {!resumeFile ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-600 rounded-xl p-6 text-center cursor-pointer hover:border-indigo-500/50 transition-colors group"
                      data-testid="public-test-resume-dropzone"
                    >
                      <Upload className="h-8 w-8 mx-auto mb-2 text-slate-500 group-hover:text-indigo-400 transition-colors" />
                      <p className="text-sm text-slate-400 group-hover:text-slate-300">
                        Click to upload your resume
                      </p>
                      <p className="text-xs text-slate-600 mt-1">PDF or Word (max 10MB)</p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3 rounded-lg border border-indigo-500/30 bg-indigo-500/5">
                      <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0">
                        <FileText className="h-5 w-5 text-indigo-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-200 truncate">{resumeFile.name}</p>
                        <p className="text-xs text-slate-500">{(resumeFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-red-400"
                        onClick={() => { setResumeFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        data-testid="public-test-remove-resume-btn"
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white font-semibold text-sm"
                  data-testid="public-test-continue-btn"
                >
                  Continue <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step: Agreement */}
        {step === 'agreement' && (
          <Card className="bg-[#1e293b] border-slate-700/50" data-testid="public-test-agreement-card">
            <CardContent className="p-6 sm:p-8">
              <div className="mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-100">Assessment Agreement</h2>
                  <p className="text-xs text-slate-500">Please read carefully before proceeding</p>
                </div>
              </div>
              <div className="bg-[#0f172a] rounded-lg p-5 mb-6 max-h-[300px] overflow-y-auto border border-slate-700/50">
                <pre className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'inherit' }}>
                  {AGREEMENT_TEXT}
                </pre>
              </div>
              <div className="flex items-start gap-3 mb-6 p-3 rounded-lg bg-slate-800/50">
                <Checkbox
                  id="agreement"
                  checked={agreed}
                  onCheckedChange={setAgreed}
                  className="mt-0.5"
                  data-testid="public-test-agreement-checkbox"
                />
                <label htmlFor="agreement" className="text-sm text-slate-300 cursor-pointer leading-relaxed">
                  I have read and agree to the terms above. I understand that my session will be monitored and my data will be shared with the hiring team.
                </label>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 h-11 border-slate-600 text-slate-300 hover:bg-slate-700/50"
                  onClick={() => { setStep('register'); setAgreed(false); }}
                  data-testid="public-test-back-btn"
                >
                  Back
                </Button>
                <Button
                  className="flex-1 h-11 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white font-semibold"
                  onClick={handleAgreementAccept}
                  disabled={!agreed || submitting}
                  data-testid="public-test-accept-btn"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                  ) : (
                    <>Accept & Start <ArrowRight className="h-4 w-4 ml-2" /></>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step: Submitting */}
        {step === 'submitting' && (
          <Card className="bg-[#1e293b] border-slate-700/50">
            <CardContent className="py-16 text-center">
              <Loader2 className="h-10 w-10 animate-spin text-indigo-400 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-slate-100 mb-2">Setting up your assessment...</h2>
              <p className="text-sm text-slate-400">Processing your resume and preparing interview questions</p>
              <Progress value={60} className="max-w-xs mx-auto mt-6 h-1.5" />
            </CardContent>
          </Card>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <Card className="bg-[#1e293b] border-emerald-500/20" data-testid="public-test-success-card">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-slate-100 mb-2">You're All Set!</h2>
              <p className="text-sm text-slate-400 mb-4">Redirecting you to the interview in a moment...</p>
              <Loader2 className="h-5 w-5 animate-spin text-indigo-400 mx-auto" />
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-[11px] text-slate-600 mt-8">
          Powered by HireFast AI &mdash; Secure & Monitored Assessment
        </p>
      </div>
    </div>
  );
}
